import ym from 'react-yandex-metrika';

import type { CallEffect } from '@redux-saga/core/effects';
import {
    postPublishClesson,
    postLessonAssigns,
    unpublishCLesson,
    addLessonToCourse,
    postClessonFinish,
    postAddStudentsToLesson,
    getTime,
    putSubscribeCourse,
    patchTeamSyncStatus,
} from '@yandex-int/k-common/client/api';
import { apiConfig } from '@yandex-int/k-common/client/api/config';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import type { CLESSON_MODES } from '@yandex-int/k-common/constants';
import type {
    CLesson,
    FullCLesson,
    AttributesType,
    Team,
    LessonSubscription,
    LESSONS_TYPES,
    LessonProblem,
} from '@yandex-int/k-common/typings';
import { ATTRIBUTES, EXTERNAL_LMS_SLUGS } from '@yandex-int/k-common/typings';
import { addMinutes } from 'date-fns';
import isEqual from 'lodash/isEqual';
import { showNetworkError, showIntlMessageNotification } from 'platform-components';
import { NOTIFICATION_THEMES, GRAVITIES } from 'platform-components/constants';
import { getExactPathMatch } from 'platform-components/utils';
import { all, call, put, race, select, take, takeLatest, fork, spawn } from 'redux-saga/effects';
import { stringify } from 'query-string';

import * as closingLessonModalActions from 'common.components/closing-lesson-modal/actions';
import { CONFIRM_LESSON_CLOSING, CANCEL_LESSON_CLOSING } from 'common.components/closing-lesson-modal/constants';
import { COMMON_NOTIFICATION_DELAY } from 'common.components/constants';
import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { actions as lessonActions } from 'common.components/lesson';
import { setAssignmentState } from 'common.components/lesson-view.assignment/actions';
import * as lessonAssignmentConstants from 'common.components/lesson-view.assignment/constants';
import { ASSIGNMENT_STATES } from 'common.components/lesson-view.assignment/typings';
import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import { getShouldLoadPrepLessonDetails } from 'common.components/lesson-view.store/selectors';
import { subscriptionModalActions } from 'common.components/lesson-view.subscription-modal/actions';
import { SUBSCRIPTION_MODAL_MODE } from 'common.components/lesson-view.subscription-modal/typings';
import { emitSocketForceCompleteLesson } from 'common.components/lesson/sagas/';
import { getLesson } from 'common.components/lesson/sagas/getLesson';
import { saveCLesson } from 'common.components/lesson/sagas/saveLesson';
import { handleDeleteOrEditError } from 'common.components/lesson/sagas/utils';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { getLessonProblemIds } from 'common.components/lesson/selectors';
import { getLessonDefaultName, getThemeId } from 'common.components/lesson/utils';
import { getPathName } from 'common.components/pathname/selectors';
import { addViewedHint, setWizardVariables } from 'common.components/user/actions';
import { getAssignedLessonsCount, getCurrentClass } from 'common.components/user/selectors';
import { ASSIGNED_LESSONS_COUNT, LESSON_ASSIGNED } from 'common.components/user/user.constants';
import { getDefaultCourseId } from 'store/courses/selectors';
import { diagnosticsActions } from 'store/diagnostics/actions';
import * as diagnosticsSelectors from 'store/diagnostics/selectors';
import { getDiagnosticProblemIds } from 'store/diagnostics/selectors';
import { Diagnostic } from 'store/diagnostics/typings';
import { loadPrepLessonsDetails } from 'store/prep-lessons/saga';
import { actions as problemsViewActions } from 'store/problems-view/actions';
import * as problemsViewSelectors from 'store/problems-view/selectors';
import * as problemSelectors from 'store/problems/selectors';
import { getFullUrl } from 'utils/get-full-url';
import { getIsLessonEvaluable } from 'utils/lesson';
import { getContainerSlugFromPath, getSubjectFromPath, getTeamIdFromPath } from 'utils/path';

import { getOpenedCourseContainerSlug } from '../../../bundles/lab/components/library.course-container/selectors';
import { actions as courseContainerActions } from '../../widgets.course-container/actions';

import * as lessonViewActions from '../actions';
import { setLessonCourseId } from '../actions';
import * as constants from '../constants';
import * as selectors from '../selectors';
import { LESSON_CONTENT_MODE, PROCESSABLE_ERRORS } from '../typings';
import { getAssignLessonToast, mapFrontAssignmentInfoToBack } from '../utils';

import { getProblemStacks } from './getProblemStacks';
import { setLessonNameByModeSaga } from './setLessonDefaultName';
import { setLessonModeSaga } from './setLessonMode';

let assignRetriesCount = 0;
const MAX_ASSIGN_RETIES = 3;

export function* lessonViewSagasWatcher() {
    yield all([
        takeLatest(constants.ADD_STUDENTS_TO_LESSON_REQUEST, addStudentsToLessonSaga),
        takeLatest(constants.ASSIGN_LESSON_REQUEST, assignLessonSaga),
        takeLatest(constants.FORCE_COMPLETE_LESSON, forceCompleteLessonSaga),
        takeLatest(constants.CHANGE_PROBLEM_VARIANT, changeProblemVariantSaga),
        takeLatest(lessonAssignmentConstants.SET_LESSON_MODE, setLessonModeSaga),
        takeLatest(constants.SET_LESSON_NAME_BY_MODE, setLessonNameByModeSaga),
        takeLatest(constants.GET_PROBLEM_STACKS_REQUEST, getProblemStacks),
        takeLatest(constants.LOAD_LESSON_VIEW_DATA, loadLessonViewDataSaga),
    ]);
}

// eslint-disable-next-line complexity
export function* assignLessonSaga(action: ReturnType<typeof lessonViewActions.assignLesson>) {
    assignRetriesCount = 0;
    let lessonId: number | undefined;
    let clessonId = 0;

    try {
        yield put(lessonActions.setLessonAssignmentStarted());
        let { onAssign, intl, assignmentInfo, shouldShowSubscriptionModal, mesBackUrl, isTeacherFromMes } = action;

        // если есть необходимость - показываем модалку подписку
        if (shouldShowSubscriptionModal) {
            yield put(
                subscriptionModalActions.toggleSubscriptionModal({
                    opened: true,
                    mode: SUBSCRIPTION_MODAL_MODE.SUBSCRIBE,
                })
            );
            yield race({
                confirm: take(subscriptionModalActions.confirmSubscriptionRequest),
                cancel: take(subscriptionModalActions.cancelSubscription),
            });
        }

        const themeId = getThemeId();
        let assignedLessonsCount: number = yield select(getAssignedLessonsCount);
        const isPreparedLesson: boolean = yield select(lessonSelectors.getIsPreparedLesson);
        const isLessonNamedByDefault: boolean = yield select(lessonSelectors.getIsLessonNamedByDefault, { intl });
        let courseId: number | null = yield select(getCurrentYearCourseId);
        const teamId = getTeamIdFromPath();

        if (isPreparedLesson) {
            const status: LOADING_STATUSES = yield select(lessonSelectors.getAddLessonToCourseStatus);
            if (status !== LOADING_STATUSES.LOADING) {
                const lesson: FullCLesson = yield select(lessonSelectors.getLesson);

                const openedCourseContainerSlug: string | null = yield select(getOpenedCourseContainerSlug);
                const containerSlug = getContainerSlugFromPath() ?? openedCourseContainerSlug;

                const moduleId: number = yield select(lessonSelectors.getLessonModuleId);
                const lessonSource: string | null = yield select(lessonSelectors.getLessonSource);
                const attributes: AttributesType = {} as AttributesType;

                if (lessonSource) {
                    attributes[ATTRIBUTES.LESSON_SOURCE] = lessonSource;
                }

                if (!courseId && lesson.subject) {
                    courseId = yield select(getDefaultCourseId, {
                        subject: lesson.subject,
                        classId: teamId,
                    });
                }

                yield put(lessonActions.clearLessonSource());
                lessonId = lesson.id;

                const { clesson_id: id } = yield call(addLessonToCourse, {
                    attributes,
                    courseId: courseId!,
                    lessonId: lessonId!,
                    themeId: themeId!,
                    moduleId,
                    containerSlug,
                });

                // привязываем курс контейнер к курсу при выдаче на странице КК
                if (containerSlug && courseId) {
                    ym('reachGoal', 'teacher_info_course_assign', { slug: containerSlug });
                    yield put(
                        courseContainerActions.postLinkCourseContainerToCourseRequest({
                            courseContainerSlugs: [containerSlug],
                            courseId,
                        })
                    );
                }

                yield put(
                    lessonActions.addedLessonToCourse({
                        courseId,
                        teamId,
                        containerSlug,
                    })
                );
                clessonId = id;
            } else {
                yield take(lessonActions.setAddLessonToCourseSuccess);

                clessonId = yield select(lessonSelectors.getCLessonId);
            }
        } else {
            clessonId = yield select(lessonSelectors.getCLessonId);
            const isDraft: boolean = yield select(lessonSelectors.getIsDraft);
            if (!isDraft) {
                yield call(unpublishCLesson, clessonId);
                // урок распубликован - декрементим кол-во выдач
                assignedLessonsCount -= 1;
            }
        }

        const lesson: FullCLesson = yield call(assignLesson, action, clessonId);
        // активируем короткую подписку, если у урока она есть
        yield activateShortSubscription(courseId!);
        if (!lesson.clesson) {
            return;
        }

        const newLessonName = getLessonDefaultName({
            intl,
            isEvaluable: getIsLessonEvaluable(assignmentInfo.lessonMode),
        });

        if (isLessonNamedByDefault && lesson.name !== newLessonName) {
            yield put(lessonActions.setLesson({ lesson }));
            yield put(lessonActions.setName({ lessonName: newLessonName }));
            yield saveCLesson();
        }

        yield call(
            finishAssignment,
            action,
            assignedLessonsCount,
            lessonId,
            clessonId,
            lesson.clesson,
            mesBackUrl,
            isTeacherFromMes
        );

        onAssign(lesson.clesson.id);
        if (isTeacherFromMes) {
            yield call(patchTeamSyncStatus, { lms: EXTERNAL_LMS_SLUGS.MES, teamIds: [teamId!] });
        }
    } catch (error) {
        const { onError } = action;

        if (onError) {
            onError();
        }

        console.error(error);
        yield handleDeleteOrEditError(error, clessonId);

        yield put(lessonActions.lessonAssignError({ error }));
    }
}

export function* assignLesson(
    action: ReturnType<typeof lessonViewActions.assignLesson>,
    clessonId: number
): Generator<any, FullCLesson | null, any> {
    try {
        let {
            assignmentInfo: { assignmentTable },
            assignmentInfo,
        } = action;
        const courseId: number = yield select(getCurrentYearCourseId);

        yield call(postLessonAssigns, { assignments: assignmentTable, clessonId, courseId });

        const now: string | number = yield call(getTime);
        const requestData = mapFrontAssignmentInfoToBack(assignmentInfo, new Date(now));
        return (yield call(postPublishClesson, clessonId, requestData)) as FullCLesson;
    } catch (error) {
        if (error && error.status === 504 && assignRetriesCount <= MAX_ASSIGN_RETIES) {
            assignRetriesCount += 1;

            return (yield call(assignLesson, action, clessonId)) as FullCLesson;
        }

        const { onAssignError } = action;

        if (error && Object.values(PROCESSABLE_ERRORS).includes(error.data.errors[0].message)) {
            if (onAssignError) {
                onAssignError(error.data.errors[0].message);
            }
        } else {
            console.error(error);
            yield put(showNetworkError(error));
        }

        yield put(lessonActions.lessonAssignError({ error }));

        return null;
    }
}

// eslint-disable-next-line complexity,max-params
export function* finishAssignment(
    action: ReturnType<typeof lessonViewActions.assignLesson>,
    newAssignedLessonsCount: number,
    lessonId: number | undefined,
    clessonId: number,
    clesson: CLesson,
    mesBackUrl: string | null,
    isTeacherFromMes: boolean
) {
    try {
        const { getNotificationMessageCallback, notificationMessageId } = action;
        const teamId = getTeamIdFromPath();
        const courseId: number = yield select(getCurrentYearCourseId);
        const activeSubscriptionSlug: string = yield select(lessonSelectors.getLessonActiveSubscriptionSlug, {
            courseId,
        });
        const lessonProblemIds: number[] = yield select(getLessonProblemIds);
        const lessonType: LESSONS_TYPES = yield select(lessonSelectors.getLessonType);
        const isPreparedLesson: boolean = yield select(lessonSelectors.getIsPreparedLesson);
        const contentMode: LESSON_CONTENT_MODE = yield select(lessonViewSelectors.getLessonContentMode);
        const staticUrls = apiConfig.getStaticUrls();

        const subject = getSubjectFromPath();
        const diagnosticLesson: FullCLesson | null | undefined = yield select(
            diagnosticsSelectors.getDiagnosticLesson,
            { subject }
        );
        const diagnostic: Diagnostic | undefined = yield select(diagnosticsSelectors.getDiagnostic, { subject });

        const {
            id: assignedLessonId,
            date_assignment: dateAssignmentString,
            date_deadline: dateDeadlineString,
            finish_date: dateFinishString,
            mode,
            duration,
        } = clesson;
        const dateAssignment = dateAssignmentString ? new Date(dateAssignmentString) : null;
        const dateDeadline = dateDeadlineString ? new Date(dateDeadlineString) : null;
        const dateFinish = dateFinishString ? new Date(dateFinishString) : null;

        yield put(setWizardVariables({ [LESSON_ASSIGNED]: true }));
        yield put(addViewedHint(ASSIGNED_LESSONS_COUNT, newAssignedLessonsCount + 1));

        yield put(lessonActions.clearEditor());
        yield put(lessonActions.lessonAssignSuccess());
        yield put(lessonActions.lessonUpdated());

        const diagnosticProblemIds: number[] = yield select(getDiagnosticProblemIds, { subject });
        const isDiagnosticLesson =
            diagnosticLesson?.clesson?.id === assignedLessonId || isEqual(lessonProblemIds, diagnosticProblemIds);
        const diagnosticClessonId = diagnosticLesson?.clesson?.id || assignedLessonId || undefined;

        if (diagnostic && isDiagnosticLesson && teamId) {
            yield put(
                diagnosticsActions.postDiagnosticAssignedRequest({
                    diagnosticId: diagnostic.id as any, // TODO: string vs number
                    courseId,
                    courseLessonLinkId: diagnosticClessonId!,
                    teamId,
                })
            );
            yield put(diagnosticsActions.clearDiagnosticStoreStateBySubject({ subject, teamId }));
        }

        // для выдачи первого занятия отображается большая модалка - тост не показываем EDUCATION-10969
        if (newAssignedLessonsCount > 0 && clessonId && teamId) {
            const team: Team | null = yield select(getCurrentClass, { classId: teamId });

            const studentLink = isTeacherFromMes
                ? `${getFullUrl(staticUrls.externalKids)}?${stringify({
                      course: courseId,
                      lesson: clessonId,
                      class_id: teamId,
                  })}`
                : null;

            yield spawn(function* () {
                const notificationAction: Awaited<ReturnType<typeof getNotificationMessageCallback>> = yield call(
                    getNotificationMessageCallback,
                    assignedLessonId,
                    mesBackUrl,
                    studentLink,
                    dateDeadline
                );

                yield put(
                    showIntlMessageNotification({
                        ...getAssignLessonToast(
                            dateAssignment!,
                            getIsLessonEvaluable(mode) ? addMinutes(dateFinish!, -duration!) : dateDeadline
                        ),
                        headerValues: {
                            level: team?.level,
                            name: team?.name,
                        },
                        notificationAction,
                        notificationMessageId,
                        theme: NOTIFICATION_THEMES.TOAST,
                        withExplicitClose: true,
                        gravity: GRAVITIES.BOTTOM,
                        mix: 'lesson-view__assignment-toast',
                    })
                );
            });
        }

        const contentType = resolveYaMetrikaContentType({ contentMode, isPreparedLesson, lessonType });
        yield call(callYaMetrika, {
            activeSubscriptionSlug,
            contentType,
            clessonId,
            diagnosticLessonId: diagnosticClessonId,
            mode,
        });
    } catch (error) {
        console.error(error);

        yield put(showNetworkError(error));
        yield put(lessonActions.lessonAssignError(error));
    }
}

export function* callYaMetrika(args: {
    activeSubscriptionSlug: string | null;
    contentType: string;
    clessonId: number;
    diagnosticLessonId: number | undefined;
    mode: CLESSON_MODES;
}) {
    const { activeSubscriptionSlug, contentType, clessonId, diagnosticLessonId, mode } = args;

    const staticUrls = apiConfig.getStaticUrls();
    const pathname = getPathName();
    const exactPathMatch = pathname ? getExactPathMatch(staticUrls, pathname) : null;
    const { subject } = exactPathMatch?.params || {};

    ym('reachGoal', 'AssignmentsLesson', {
        clessonId,
        contentType,
        subject,
        mode,
        subscription_slug: activeSubscriptionSlug,
    });

    const courseId: number | null = yield select(getCurrentYearCourseId);

    if (diagnosticLessonId && diagnosticLessonId === clessonId) {
        ym('reachGoal', 'diagnostic_modal_published', {
            course_id: courseId,
            subject,
            clessonId,
        });
    }
}

export function* addStudentsToLessonSaga(action: ReturnType<typeof lessonViewActions.addStudentsToLesson>) {
    try {
        const { addedStudents, clessonId, onSuccess } = action.payload;

        if (addedStudents.length === 0) {
            yield put(lessonViewActions.addStudentsToLessonSuccess());

            if (onSuccess) {
                onSuccess();
            }

            return;
        }

        const lesson: FullCLesson = yield select(lessonSelectors.getLesson);

        yield call(postAddStudentsToLesson, { clessonId, studentIds: addedStudents });
        yield put(lessonViewActions.addStudentsToLessonSuccess());

        yield put(
            showIntlMessageNotification({
                headerId: 'toast.addedStudentsToLesson.header',
                messageId: 'toast.addedStudentsToLesson.description',
                messageValues: {
                    lessonName: lesson.name,
                    studentsCount: addedStudents.length,
                },
                theme: NOTIFICATION_THEMES.TOAST,
                withExplicitClose: true,
                closeDelay: COMMON_NOTIFICATION_DELAY,
                gravity: GRAVITIES.BOTTOM,
            })
        );

        if (onSuccess) {
            onSuccess();
        }
    } catch (error) {
        console.error(error);

        yield put(showNetworkError(error));
        yield put(lessonViewActions.addStudentsToLessonError());
    }
}

function* loadLessonViewDataSaga(action: ReturnType<typeof lessonViewActions.loadLessonViewData>) {
    const { courseId = null, lessonId, moduleId = null, onSuccess } = action.payload;

    yield put(setAssignmentState(ASSIGNMENT_STATES.LOADING));

    const callEffects: CallEffect[] = [
        call(getLesson, {
            lessonId,
            moduleId,
        }),
    ];

    const shouldLoadPrepLessonDetails: boolean = yield select(getShouldLoadPrepLessonDetails, {
        courseId,
        lessonId,
    });

    if (courseId && shouldLoadPrepLessonDetails) {
        callEffects.push(
            call(loadPrepLessonsDetails, {
                courseId,
                lessonsIds: [lessonId],
            })
        );
    }

    if (courseId) {
        yield put(setLessonCourseId({ courseId }));
    }

    yield all(callEffects);

    if (onSuccess) {
        onSuccess();
    }
}

function* forceCompleteLessonSaga(action: ReturnType<typeof lessonViewActions.forceCompleteLesson>) {
    try {
        const { courseId, clessonId, io, websocketDomain, onSuccess } = action;

        yield put(closingLessonModalActions.toggleModal(true));
        const { confirm } = yield race({
            confirm: take(CONFIRM_LESSON_CLOSING),
            cancel: take(CANCEL_LESSON_CLOSING),
        });

        if (confirm) {
            yield call(postClessonFinish, clessonId);
            yield fork(emitSocketForceCompleteLesson, courseId, clessonId, io, websocketDomain);
            yield put(lessonActions.getClessonWithResultsRequest({ courseId, clessonId }));
            yield put(closingLessonModalActions.toggleModal(false));
            yield put(lessonActions.lessonUpdated());
            const lessonName: string | null = yield select(lessonSelectors.getName);
            yield put(
                showIntlMessageNotification({
                    closeDelay: COMMON_NOTIFICATION_DELAY,
                    gravity: GRAVITIES.BOTTOM,
                    messageId: 'toast.lessonModal.notification.finishForced',
                    messageValues: { lessonName },
                    withExplicitClose: true,
                    theme: NOTIFICATION_THEMES.TOAST,
                })
            );

            if (onSuccess) {
                onSuccess();
            }
        } else {
            yield put(closingLessonModalActions.toggleModal(false));
        }
    } catch (error) {
        console.error(error);
        yield put(showNetworkError(error));
    }
}

function* changeProblemVariantSaga(action: ReturnType<typeof lessonViewActions.changeProblemVariant>) {
    try {
        const { newProblemId } = action;

        const stackId: number = yield select(selectors.getProblemStackId);
        const oldProblemId: number = yield select(problemsViewSelectors.getSelectedProblemId);
        const problems: LessonProblem[] = yield select(problemSelectors.getProblemsByStackId, { stackId });

        const newProblem = problems.find((problem) => problem.id === newProblemId)!;

        yield put(lessonActions.changeProblemVariant({ oldProblemId, newProblem, stackId }));
        yield put(problemsViewActions.setSelectedProblemId(newProblemId));
        yield put(lessonActions.saveLesson());
    } catch (error) {
        console.error(error);
        yield put(showNetworkError(error));
    }
}

function resolveYaMetrikaContentType(args: {
    contentMode: LESSON_CONTENT_MODE;
    isPreparedLesson: boolean;
    lessonType: string;
}) {
    const { contentMode, isPreparedLesson, lessonType } = args;

    if (contentMode === LESSON_CONTENT_MODE.CUSTOM_VIEW) {
        return 'arrayOfLessons';
    }

    if (isPreparedLesson) {
        return 'preparedLesson';
    }

    return lessonType;
}

function* activateShortSubscription(courseId: number) {
    try {
        const shortSubscriptions: Array<LessonSubscription> = yield select(lessonSelectors.getLessonShortSubscriptions);
        if (shortSubscriptions.length > 0) {
            const firstSubscriptionSlug = shortSubscriptions[0].subscription.slug;
            yield call(putSubscribeCourse, { slug: firstSubscriptionSlug, target: { course_id: courseId } });
        }
    } catch (e) {
        console.error(e);
        yield put(showNetworkError());
    }
}
